import { useCallback, useEffect, useRef, useState } from "react";

import { appendDictationTranscript } from "./careerPlanOnboarding";

type SpeechRecognitionCtor = new () => SpeechRecognitionHandle;

type SpeechRecognitionHandle = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: { results: ArrayLike<{ isFinal: boolean; 0?: { transcript?: string } }> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | undefined {
  const speechWindow = window as Window & {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
}

function transcriptFromResults(results: ArrayLike<{ isFinal: boolean; 0?: { transcript?: string } }>): string {
  let text = "";
  for (let index = 0; index < results.length; index += 1) {
    text = appendDictationTranscript(text, results[index]?.[0]?.transcript ?? "");
  }
  return text;
}

export default function useSpeechToText({
  value,
  onChange,
  onUnsupported,
  onError
}: {
  value: string;
  onChange: (next: string) => void;
  onUnsupported: () => void;
  onError: (error: string) => void;
}) {
  const [listening, setListening] = useState(false);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const onErrorRef = useRef(onError);
  const recognitionRef = useRef<SpeechRecognitionHandle | null>(null);
  const shouldListenRef = useRef(false);
  const snapshotRef = useRef(value);

  valueRef.current = value;
  onChangeRef.current = onChange;
  onErrorRef.current = onError;

  const stop = useCallback(() => {
    shouldListenRef.current = false;
    setListening(false);
    recognitionRef.current?.stop();
  }, []);

  const toggle = useCallback(() => {
    if (shouldListenRef.current || listening) {
      stop();
      return;
    }

    const SpeechRecognitionImpl = getSpeechRecognitionCtor();
    if (!SpeechRecognitionImpl) {
      onUnsupported();
      return;
    }

    const previous = recognitionRef.current;
    if (previous) {
      previous.onend = null;
      previous.abort();
      recognitionRef.current = null;
    }

    const recognition = new SpeechRecognitionImpl();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || "en-US";
    recognition.onresult = (event) => {
      onChangeRef.current(appendDictationTranscript(snapshotRef.current, transcriptFromResults(event.results)));
    };
    recognition.onerror = (event) => {
      onErrorRef.current(event.error);
      if (event.error === "aborted" || event.error === "no-speech") return;
      shouldListenRef.current = false;
      setListening(false);
    };
    recognition.onend = () => {
      if (recognitionRef.current !== recognition) return;
      if (!shouldListenRef.current) {
        setListening(false);
        recognitionRef.current = null;
        return;
      }
      snapshotRef.current = valueRef.current;
      try {
        recognition.start();
      } catch {
        shouldListenRef.current = false;
        setListening(false);
        recognitionRef.current = null;
        onErrorRef.current("network");
      }
    };

    recognitionRef.current = recognition;
    shouldListenRef.current = true;
    snapshotRef.current = valueRef.current;
    setListening(true);
    try {
      recognition.start();
    } catch {
      shouldListenRef.current = false;
      setListening(false);
      recognitionRef.current = null;
      onErrorRef.current("network");
    }
  }, [listening, onUnsupported, stop]);

  useEffect(() => {
    return () => {
      shouldListenRef.current = false;
      recognitionRef.current?.abort();
    };
  }, []);

  return { listening, toggle };
}
